/**
 * F10 — Admin venue submission review page.
 *
 * Route: /venues/submissions/:id
 * Permission: venues:moderate
 *
 * Read-only review by default. An "Edit details" toggle lets the admin correct
 * the name / phone / WhatsApp / street address / description and drag the map
 * pin to fix the exact location BEFORE approving. Approve sends only the changed
 * fields as `overrides`; the API merges them into the submission payload and
 * creates the live venue. Right pane shows the location on a map + any
 * potential-duplicate candidate. Actions: Approve / Reject / Merge.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  OsmMapPicker,
  Badge,
  Banner,
  Button,
  Card,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Modal,
  Select,
  Spinner,
  TextInput,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { SPORT_NAME_VALUES, type SportName } from '@bravemove/types';
import {
  adminVenueApi,
  type ApproveVenueOverrides,
  type VenueSubmissionForAdmin,
} from '../api/venue.js';
import { getGeo } from '../api/geo';
import { translateApiError } from '../auth/errors.js';
import { PageHeader } from '../components/PageHeader';

type Bbox = { minLat: number; maxLat: number; minLng: number; maxLng: number };
const LEBANON_BBOX: Bbox = { minLat: 33.05, maxLat: 34.7, minLng: 35.1, maxLng: 36.7 };

const ReasonSchema = z.object({ reason: z.string().min(10).max(500) });
type ReasonValues = z.infer<typeof ReasonSchema>;

type Payload = VenueSubmissionForAdmin['payload'];

interface Draft {
  name: string;
  phone: string;
  whatsappPhone: string;
  streetAddress: string;
  description: string;
  lat: number;
  lng: number;
  sports: SportName[];
  primarySport: SportName | undefined;
}

function draftFromPayload(p: Payload): Draft {
  return {
    name: p.name,
    phone: p.phone,
    whatsappPhone: p.whatsappPhone ?? '',
    streetAddress: p.streetAddress ?? '',
    description: p.description ?? '',
    lat: p.lat,
    lng: p.lng,
    sports: [...p.sports],
    primarySport: p.primarySport,
  };
}

const round6 = (n: number) => Number(n.toFixed(6));

/** Build the overrides object from the draft vs the original payload (changed keys only). */
function computeOverrides(d: Draft, p: Payload): ApproveVenueOverrides {
  const o: Record<string, unknown> = {};
  const norm = (s: string) => s.trim();
  if (norm(d.name) && norm(d.name) !== p.name) o.name = norm(d.name);
  if (norm(d.phone) && norm(d.phone) !== p.phone) o.phone = norm(d.phone);
  if (norm(d.whatsappPhone) !== (p.whatsappPhone ?? '')) {
    o.whatsappPhone = norm(d.whatsappPhone) || null;
  }
  if (norm(d.streetAddress) !== (p.streetAddress ?? '')) {
    o.streetAddress = norm(d.streetAddress) || null;
  }
  if (norm(d.description) !== (p.description ?? '')) {
    o.description = norm(d.description) || null;
  }
  if (round6(d.lat) !== round6(p.lat)) o.lat = round6(d.lat);
  if (round6(d.lng) !== round6(p.lng)) o.lng = round6(d.lng);
  const sportsChanged =
    d.sports.length !== p.sports.length || d.sports.some((s, i) => s !== p.sports[i]);
  if (sportsChanged) o.sports = [...d.sports];
  // Keep primarySport consistent with sports whenever either changes.
  if (sportsChanged || (d.primarySport && d.primarySport !== p.primarySport)) {
    if (d.primarySport) o.primarySport = d.primarySport;
  }
  return o as ApproveVenueOverrides;
}

// ─── Sports editor ────────────────────────────────────────────────────────────

function SportsEditor({
  sports,
  primarySport,
  onChange,
}: {
  sports: SportName[];
  primarySport: SportName | undefined;
  onChange: (sports: SportName[], primarySport: SportName | undefined) => void;
}) {
  const { t } = useI18n();
  const available = SPORT_NAME_VALUES.filter((s) => !sports.includes(s));
  const remove = (s: SportName) => {
    if (sports.length <= 1) return;
    const next = sports.filter((x) => x !== s);
    onChange(next, primarySport === s ? next[0] : primarySport);
  };
  const add = (s: string) => {
    if (!s) return;
    const sport = s as SportName;
    if (sports.includes(sport)) return;
    onChange([...sports, sport], primarySport ?? sport);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {sports.map((s) => (
          <Chip
            key={s}
            selected={s === primarySport}
            onClick={() => onChange(sports, s)}
            className="cursor-pointer"
            {...(sports.length > 1 ? { onDismiss: () => remove(s) } : {})}
          >
            {s}
          </Chip>
        ))}
      </div>
      {available.length > 0 ? (
        <Select
          value=""
          onChange={(e) => add(e.target.value)}
          options={[
            {
              value: '',
              label: t('admin:venue.submission.addSport', { defaultValue: '+ Add sport…' }),
            },
            ...available.map((s) => ({ value: s, label: s })),
          ]}
        />
      ) : null}
      <p className="text-[11px] text-ink-3">
        {t('admin:venue.submission.sportsHint', {
          defaultValue: 'Tap a sport to make it primary; × removes it.',
        })}
      </p>
    </div>
  );
}

// ─── Submission fields (left) ─────────────────────────────────────────────────

function FieldsPanel({
  submission,
  draft,
  editing,
  onChange,
}: {
  submission: VenueSubmissionForAdmin;
  draft: Draft;
  editing: boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const { t } = useI18n();
  const p = submission.payload;

  return (
    <Card>
      <h3 className="mb-3 text-[13px] font-semibold text-ink">
        {t('admin:venue.submission.payload')}
      </h3>

      {editing ? (
        <div className="space-y-3">
          <FormField label={t('admin:venue.fields.name')}>
            <TextInput value={draft.name} onChange={(e) => onChange({ name: e.target.value })} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label={t('admin:venue.fields.phone')}>
              <TextInput
                value={draft.phone}
                onChange={(e) => onChange({ phone: e.target.value })}
              />
            </FormField>
            <FormField label={t('admin:venue.fields.whatsappPhone')}>
              <TextInput
                value={draft.whatsappPhone}
                onChange={(e) => onChange({ whatsappPhone: e.target.value })}
              />
            </FormField>
          </div>
          <FormField
            label={t('admin:venue.fields.streetAddress', { defaultValue: 'Street address' })}
          >
            <TextInput
              value={draft.streetAddress}
              onChange={(e) => onChange({ streetAddress: e.target.value })}
            />
          </FormField>
          <FormField label={t('admin:venue.fields.description')}>
            <Textarea
              rows={3}
              maxLength={500}
              showCounter
              value={draft.description}
              onChange={(e) => onChange({ description: e.target.value })}
            />
          </FormField>

          <FormField label={t('admin:venue.fields.sports')}>
            <SportsEditor
              sports={draft.sports}
              primarySport={draft.primarySport}
              onChange={(sports, primarySport) => onChange({ sports, primarySport })}
            />
          </FormField>

          {/* Region read-only — refined on the venue page after approval. */}
          <div className="rounded-lg bg-surface-2 p-3 text-[12px]">
            <div className="mb-1 text-ink-3">{t('admin:venue.table.location')}</div>
            <div className="text-ink-2">
              {p.cazaCode} · {p.cityCode}
            </div>
            <p className="mt-2 text-[11px] text-ink-3">
              {t('admin:venue.submission.editNote', {
                defaultValue:
                  'Region, opening hours, links & photos are editable on the venue page right after approval.',
              })}
            </p>
          </div>
        </div>
      ) : (
        <dl className="space-y-2 text-[12.5px]">
          <Row label={t('admin:venue.fields.name')} value={draft.name} strong />
          <Row label={t('admin:venue.fields.phone')} value={draft.phone} />
          {draft.whatsappPhone ? (
            <Row label={t('admin:venue.fields.whatsappPhone')} value={draft.whatsappPhone} />
          ) : null}
          <Row
            label={t('admin:venue.table.location')}
            value={`${p.cazaCode} · ${p.cityCode}${draft.streetAddress ? ` · ${draft.streetAddress}` : ''}`}
          />
          <div>
            <dt className="text-ink-3">{t('admin:venue.fields.sports')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1">
              {p.sports.map((sport) => (
                <Chip key={sport} selected={sport === p.primarySport}>
                  {sport}
                </Chip>
              ))}
            </dd>
          </div>
          {draft.description ? (
            <div>
              <dt className="text-ink-3">{t('admin:venue.fields.description')}</dt>
              <dd className="mt-0.5 rounded bg-surface-2 p-2 text-[12px] text-ink">
                {draft.description}
              </dd>
            </div>
          ) : null}
        </dl>
      )}

      <div className="mt-3 border-t border-outline pt-3">
        <div className="flex items-center gap-2">
          <Badge tone={submission.status === 'pending' ? 'warning' : 'neutral'} size="sm">
            {submission.status}
          </Badge>
          {submission.potentialDuplicateOf ? (
            <Badge tone="warning" size="sm">
              {t('admin:venue.submission.potentialDuplicateBadge')}
            </Badge>
          ) : null}
        </div>
        <p className="mt-2 text-[11px] text-ink-3">
          {`${t('admin:venue.submission.submittedMeta', { defaultValue: 'Submitted' })} ${new Date(
            submission.createdAt,
          ).toLocaleDateString()} · ${
            submission.submitterEmail ??
            t('admin:venue.submission.anonymous', { defaultValue: 'Anonymous' })
          }`}
        </p>
      </div>
    </Card>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-ink-3">{label}</dt>
      <dd className={strong ? 'font-medium text-ink' : 'text-ink'}>{value}</dd>
    </div>
  );
}

// ─── Location (right) ─────────────────────────────────────────────────────────

function LocationPanel({
  submission,
  draft,
  editing,
  bbox,
  onChange,
}: {
  submission: VenueSubmissionForAdmin;
  draft: Draft;
  editing: boolean;
  bbox: Bbox | null;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const { t } = useI18n();
  const candidate = submission.potentialDuplicateVenue;

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-[13px] font-semibold text-ink">
            {t('admin:venue.submission.locationTitle', { defaultValue: 'Location' })}
          </h3>
          <span className="font-mono text-[11px] text-ink-2">
            {draft.lat.toFixed(5)}, {draft.lng.toFixed(5)}
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-hairline">
          <OsmMapPicker
            lat={draft.lat}
            lng={draft.lng}
            editable={editing}
            bbox={bbox ?? LEBANON_BBOX}
            height={300}
            ariaLabel={t('admin:venue.submission.locationMapLabel', {
              defaultValue: 'Venue location map',
            })}
            onChange={(c) => onChange({ lat: c.lat, lng: c.lng })}
          />
        </div>
        <p className="mt-2 text-[11px] text-ink-3">
          {editing
            ? t('admin:venue.submission.mapEditHint', {
                defaultValue:
                  'Drag the pin to correct the exact location (must stay within the caza).',
              })
            : t('admin:venue.submission.mapViewHint', {
                defaultValue: 'Submitted location. Use “Edit details” to adjust the pin.',
              })}
        </p>
      </Card>

      {/* Potential-duplicate candidate */}
      <Card>
        <h3 className="mb-3 text-[13px] font-semibold text-ink">
          {t('admin:venue.submission.candidateVenue')}
        </h3>
        {candidate ? (
          <dl className="space-y-2 text-[12.5px]">
            <Row label={t('admin:venue.fields.name')} value={candidate.name} strong />
            <div>
              <dt className="text-ink-3">{t('admin:venue.fields.slug')}</dt>
              <dd className="font-mono text-ink-2">/v/{candidate.slug}</dd>
            </div>
          </dl>
        ) : (
          <EmptyState
            icon={<UIIcon name="pin" size={24} color="currentColor" />}
            title={t('admin:venue.submission.noCandidate')}
          />
        )}
      </Card>
    </div>
  );
}

// ─── Reject modal ─────────────────────────────────────────────────────────────

function RejectModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (reason: string) => void;
}) {
  const { t } = useI18n();
  const form = useForm<ReasonValues>({
    resolver: zodResolver(ReasonSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) form.reset({ reason: '' });
  }, [open, form]);

  const submit = form.handleSubmit((values) => {
    onSuccess(values.reason);
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:venue.modals.rejectTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:venue.actions.reject')}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-[12.5px] text-ink-2">
        {t('admin:venue.submission.rejectExplain', {
          defaultValue:
            'Rejecting marks this submission as Rejected with your reason (kept for the record). No venue is created and nothing is published — it just leaves the pending queue.',
        })}
      </p>
      <FormField
        label={t('admin:venue.modals.rejectReasonLabel')}
        error={form.formState.errors.reason?.message}
        required
      >
        <Textarea
          rows={4}
          maxLength={500}
          showCounter
          placeholder={t('admin:venue.modals.rejectReasonPlaceholder')}
          {...form.register('reason')}
        />
      </FormField>
    </Modal>
  );
}

// ─── Merge modal ──────────────────────────────────────────────────────────────

function MergeModal({
  open,
  candidateId,
  onClose,
  onSuccess,
}: {
  open: boolean;
  candidateId: string;
  onClose: () => void;
  onSuccess: (reason: string) => void;
}) {
  const { t } = useI18n();
  const form = useForm<ReasonValues>({
    resolver: zodResolver(ReasonSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) form.reset({ reason: '' });
  }, [open, form]);

  const submit = form.handleSubmit((values) => {
    void candidateId;
    onSuccess(values.reason);
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:venue.modals.mergeTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:venue.actions.merge')}
          </Button>
        </>
      }
    >
      <FormField
        label={t('admin:venue.modals.mergeReasonLabel')}
        error={form.formState.errors.reason?.message}
        required
      >
        <Textarea
          rows={3}
          maxLength={500}
          showCounter
          placeholder={t('admin:venue.modals.mergeReasonPlaceholder')}
          {...form.register('reason')}
        />
      </FormField>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AdminVenueSubmissionReviewPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [submission, setSubmission] = useState<VenueSubmissionForAdmin | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState(false);
  const [cazaBbox, setCazaBbox] = useState<Bbox | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminVenueApi.getSubmission(id);
      setSubmission(data);
      setDraft(draftFromPayload(data.payload));
      setEditing(false);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  // Fetch the submission's caza bbox so the map pin can't be dragged outside the
  // caza — VenueService.create rejects out-of-caza coordinates on approve.
  useEffect(() => {
    if (!submission) return;
    let cancelled = false;
    const cazaCode = submission.payload.cazaCode;
    getGeo<{ data: ReadonlyArray<{ code: string; bbox: Bbox | null }> }>('/geo/cazas')
      .then((res) => {
        if (cancelled) return;
        const match = res.data.find((c) => c.code === cazaCode);
        if (match?.bbox) setCazaBbox(match.bbox);
      })
      .catch(() => {
        /* fall back to the Lebanon-wide bbox */
      });
    return () => {
      cancelled = true;
    };
  }, [submission]);

  const overrides = useMemo(
    () => (submission && draft ? computeOverrides(draft, submission.payload) : {}),
    [submission, draft],
  );
  const changedLabels = useMemo(() => {
    const labelOf = (k: string): string => {
      switch (k) {
        case 'name':
          return t('admin:venue.fields.name');
        case 'phone':
          return t('admin:venue.fields.phone');
        case 'whatsappPhone':
          return t('admin:venue.fields.whatsappPhone');
        case 'streetAddress':
          return t('admin:venue.fields.streetAddress', { defaultValue: 'Street address' });
        case 'description':
          return t('admin:venue.fields.description');
        case 'location':
          return t('admin:venue.submission.locationField', { defaultValue: 'Location' });
        case 'sports':
          return t('admin:venue.fields.sports');
        default:
          return k;
      }
    };
    const keys = new Set<string>();
    for (const k of Object.keys(overrides)) {
      keys.add(
        k === 'lat' || k === 'lng'
          ? 'location'
          : k === 'sports' || k === 'primarySport'
            ? 'sports'
            : k,
      );
    }
    return [...keys].map(labelOf);
  }, [overrides, t]);
  const isDirty = Object.keys(overrides).length > 0;

  const patchDraft = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const handleApprove = async () => {
    if (!submission) return;
    setApproveLoading(true);
    setApproveError(null);
    try {
      const result = await adminVenueApi.approveSubmission(
        submission.id,
        isDirty ? overrides : undefined,
      );
      setApproveOpen(false);
      toast.push({ tone: 'success', title: t('admin:venue.toast.approveSuccess') });
      navigate(`/venues/${result.venueId}`);
    } catch (err) {
      setApproveError(translateApiError(err, t));
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!submission) return;
    try {
      await adminVenueApi.rejectSubmission(submission.id, reason);
      setRejectOpen(false);
      toast.push({ tone: 'success', title: t('admin:venue.toast.rejectSuccess') });
      void fetchDetail();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  const handleMerge = async (reason: string) => {
    if (!submission?.potentialDuplicateOf) return;
    try {
      await adminVenueApi.mergeSubmission(submission.id, submission.potentialDuplicateOf, reason);
      setMergeOpen(false);
      toast.push({ tone: 'success', title: t('admin:venue.toast.mergeSuccess') });
      navigate(`/venues/submissions`);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} />
      </div>
    );
  }

  if (error || !submission || !draft) {
    return (
      <div className="space-y-4">
        <Banner tone="danger">{error ?? t('admin:errors.generic')}</Banner>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          {t('admin:common.back')}
        </Button>
      </div>
    );
  }

  const isPending = submission.status === 'pending';
  const hasCandidate = submission.potentialDuplicateOf !== null;

  return (
    <div className="space-y-5">
      <PageHeader title={t('admin:venue.pageTitle.submissionDetail')} />

      {/* Toolbar — edit toggle + dirty indicator */}
      {isPending ? (
        <div className="flex flex-wrap items-center gap-3">
          {editing ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                {t('admin:venue.submission.doneEditing', { defaultValue: 'Done editing' })}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(draftFromPayload(submission.payload));
                  setEditing(false);
                }}
              >
                {t('admin:venue.submission.discardEdits', { defaultValue: 'Discard edits' })}
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              {t('admin:venue.submission.editDetails', { defaultValue: 'Edit details' })}
            </Button>
          )}
          {isDirty ? (
            <Badge tone="info" size="sm">
              {t('admin:venue.submission.editedBadge', { defaultValue: 'Edited' })}
            </Badge>
          ) : null}
        </div>
      ) : null}

      {/* Split pane */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <FieldsPanel
          submission={submission}
          draft={draft}
          editing={editing}
          onChange={patchDraft}
        />
        <LocationPanel
          submission={submission}
          draft={draft}
          editing={editing}
          bbox={cazaBbox}
          onChange={patchDraft}
        />
      </div>

      {/* Action bar */}
      {isPending ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-outline pt-4">
          <Button
            variant="primary"
            onClick={() => {
              setApproveError(null);
              setApproveOpen(true);
            }}
          >
            {t('admin:venue.actions.approve')}
          </Button>
          <Button variant="destructive" onClick={() => setRejectOpen(true)}>
            {t('admin:venue.actions.reject')}
          </Button>
          {hasCandidate ? (
            <Button variant="secondary" onClick={() => setMergeOpen(true)}>
              {t('admin:venue.actions.merge')}
            </Button>
          ) : null}
          <span className="ms-auto text-[12px] text-ink-3">
            {t('admin:venue.submission.approveHint', {
              defaultValue: 'Approving publishes this as a live venue.',
            })}
          </span>
        </div>
      ) : null}

      {/* Approve dialog — explains exactly what happens */}
      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={() => void handleApprove()}
        title={t('admin:venue.modals.approveTitle')}
        loading={approveLoading}
        confirmLabel={t('admin:venue.actions.approve')}
        cancelLabel={t('admin:common.cancel')}
      >
        <p className="text-[13px] text-ink-2">
          {t('admin:venue.submission.approveExplainIntro', {
            defaultValue: 'Approving this submission will:',
          })}
        </p>
        <ul className="mt-2 space-y-1.5 text-[12.5px] text-ink-2">
          <li className="flex gap-2">
            <span className="text-accent">•</span>
            {t('admin:venue.submission.approveExplain1', {
              defaultValue:
                'Create a new public venue (Tier 1) from this data — live immediately in the catalogue and on the marketing site.',
            })}
          </li>
          <li className="flex gap-2">
            <span className="text-accent">•</span>
            {t('admin:venue.submission.approveExplain2', {
              defaultValue: 'Copy any submitted photos onto the venue.',
            })}
          </li>
          <li className="flex gap-2">
            <span className="text-accent">•</span>
            {t('admin:venue.submission.approveExplain3', {
              defaultValue: 'Mark this submission as Approved (this cannot be undone here).',
            })}
          </li>
          <li className="flex gap-2">
            <span className="text-accent">•</span>
            {t('admin:venue.submission.approveExplain4', {
              defaultValue:
                'Open the new venue’s page, where you can refine anything else (region, sports, hours, links, photos).',
            })}
          </li>
        </ul>
        {isDirty ? (
          <Banner tone="info" className="mt-3">
            {`${t('admin:venue.submission.approveEditsLabel', {
              defaultValue: 'Your edits will be applied:',
            })} ${changedLabels.join(', ')}.`}
          </Banner>
        ) : null}
        {approveError ? (
          <Banner tone="danger" className="mt-3">
            {approveError}
          </Banner>
        ) : null}
      </Dialog>

      {/* Reject modal */}
      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSuccess={(reason) => void handleReject(reason)}
      />

      {/* Merge modal */}
      {hasCandidate && submission.potentialDuplicateOf ? (
        <MergeModal
          open={mergeOpen}
          candidateId={submission.potentialDuplicateOf}
          onClose={() => setMergeOpen(false)}
          onSuccess={(reason) => void handleMerge(reason)}
        />
      ) : null}
    </div>
  );
}
